import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { FileText, Download, Search, Calendar } from "lucide-react";
import { getImageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const PLACEHOLDER_PUBS = [
  { title: "Sauyunan Foundation Company Profile 2026", category: "Annual Report", year: 2026, file_url: "/assets/sauyunan-company-profile.pdf", description: "Profil resmi Sauyunan Foundation tahun 2026" },
];

export default function Publications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Semua");
  const [q, setQ] = useState("");
  const { settings } = useSettings();
  const pageData = settings?.publications_page || {};

  useEffect(() => {
    api.get("/publications", { params: { limit: 100 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, []);

  const displayItems = items.length > 0 ? items : PLACEHOLDER_PUBS;
  const CATEGORIES = ["Semua", "Annual Report", "Financial Report", "Research", "Whitepaper", "Brochure"];

  const filtered = displayItems.filter((p) => {
    if (filter !== "Semua" && p.category !== filter) return false;
    if (q && !p.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="page-publications">
      <SeoHead title={{ id: "Publikasi", en: "Publications" }} description={{ id: pageData.description || "Laporan tahunan, riset, whitepaper, dan brosur Sauyunan Foundation.", en: "Annual reports, research, whitepapers, and brochures from Sauyunan Foundation." }} />
      <PageHeader
        eyebrow={pageData.eyebrow || "Publikasi"}
        title={pageData.title || "Laporan, riset, dan dokumen resmi"}
        description={pageData.description || "Akses laporan tahunan, laporan keuangan, dokumen riset, whitepaper, dan brosur resmi Sauyunan Foundation."}
        image={pageData.image ? getImageUrl(pageData.image) : undefined}
      />

      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="publications-search"
                type="text"
                placeholder="Cari publikasi..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition ${filter === c ? "bg-[var(--brand-blue)] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                >{c}</button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, i) => (
              <a
                key={p.id || i}
                href={p.file_url}
                target="_blank"
                rel="noreferrer"
                className="card-sf overflow-hidden group flex flex-col"
                data-testid={`publication-${i}`}
              >
                <div className="aspect-[3/2] overflow-hidden bg-gradient-to-br from-[var(--brand-blue)] to-[#254DAA] flex items-center justify-center relative">
                  {p.cover_image ? (
                    <img src={getImageUrl(p.cover_image)} alt={p.title} className="h-full w-full object-cover" />
                  ) : (
                    <FileText className="h-16 w-16 text-white/70" />
                  )}
                  <div className="absolute top-3 right-3 rounded-full bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {p.year}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{p.category}</div>
                  <h3 className="mt-1 text-lg font-bold text-slate-900 leading-tight">{p.title}</h3>
                  {p.description && <p className="mt-2 text-sm text-slate-600 line-clamp-2 flex-1">{p.description}</p>}
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500">PDF</span>
                    <span className="text-sm font-semibold text-[var(--brand-orange)] flex items-center gap-1">
                      <Download className="h-3.5 w-3.5" /> Unduh
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-24 border border-dashed border-slate-300 rounded-2xl">
              <FileText className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Tidak ada publikasi yang cocok.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
